'use client';

import { useEffect, useState } from 'react';
import { signOut, useSession } from "next-auth/react";
import Image from "next/image";
import { ButtonMenuContainer, ButtonMenuItem, ButtonMenuMenu, ButtonMenuTrigger, IconArrowDown, IconCorporations} from '@seismic/mantle';
import { dynamicImport } from "../utils/dynamicImport";
import { usePathname, useRouter } from "next/navigation";
import { setPartners } from '../slices/partnerSlice';
import { setSession, setProfiles, setPcnProperties, setCompanyProfile, setTeamsites, setContentSettings } from '../slices/sessionSlice';
import { useSelector, useDispatch } from 'react-redux';
import { Partner } from 'src/types/partner';
import { ContentSettings } from 'src/types/contentSettings';

const fallbackLogo = '/assets/logo-fallback5.png';

const Navbar = () => {
  const pathname = usePathname()
  const router = useRouter();
  const dispatch = useDispatch();
  const LinkButton = dynamicImport('LinkButton');
  const [isPermissionDenied, setIsPermissionDenied] = useState(true);
  const {data: session, status} = useSession();
  const [isSessionExpired, setIsSessionExpired] = useState(false);
  const [currentTenant, setCurrentTenant] = useState('');
  const [idToken, setIdToken] = useState<string | unknown>(null);

  const companyProfile = useSelector((state: any) => state.session.companyProfile);
  const [activeLabel, setActiveLabel] = useState('home');

  useEffect(()=>{
    const currentPath = pathname;
    let noPermissionPage = false;
    switch (currentPath) {
      case '/available-partners':
        setActiveLabel('Explore Partners');
        break;
      case '/target':
        setActiveLabel('My Connections');
        break;
      case '/profile':
        setActiveLabel('Company Profile');
        break;
      case '/content':
      case '/content/upload':
        setActiveLabel('Content Status');
        break;
      case '/insights':
        setActiveLabel('Insights');
        break;
      case '/401Error':
      case '/login':
        noPermissionPage = true;
        setActiveLabel('');
        break;
      default:
        setActiveLabel('home');
        break;
    }

    if(!noPermissionPage && session?.tenant && status === "authenticated"){
      dispatch(setSession({ ...session, contentProperties: [], profiles: [], pcnProperties: [], companyProfile: null, teamsites: [], contentSettings: null }));
      setIsPermissionDenied(false);
      setCurrentTenant(session.tenant);
      setIdToken(session.idToken);
    }else{
      setIsPermissionDenied(true);
    }

    if(!session?.expires || new Date(session.expires) < new Date()) {
      setIsSessionExpired(true);
    }else{
      setIsSessionExpired(false);
    }
  }, [dispatch, session, status, pathname])

  useEffect(() => {
    const fetchPartners = async () => {
      try {
        // Fetch partners here if needed
        const response = await fetch('/api/partners');
        const data: Partner[] = await response.json();
        dispatch(setPartners(data));
        dispatch(setCompanyProfile(data.find((partner: Partner) => partner.tenant === currentTenant) || null));
      } catch (error) {
        console.error('Failed to fetch partners:', error);
      }
    };

    const fetchContentSettings = async () => {
      try {
        const response = await fetch('/api/content-settings');
        if (!response.ok) {
          throw new Error("Failed to fetch content settings");
        }
        const newContentSettings: ContentSettings[] = await response.json();
        const contentSettings = newContentSettings.find((setting: ContentSettings) => setting.profileName === '*');
        if(contentSettings){
          dispatch(setContentSettings(contentSettings));
        }
      } catch (error) {
        console.error("Error fetching content settings:", error);
      }
    };

    const fetchProfiles = async () => {
      try {
        // Fetch Profiles
        const profilesResponse = await fetch('/api/profiles');
        if (!profilesResponse.ok) {
          console.error('Failed to fetch profiles:', profilesResponse.statusText);
          return;
        }

        const profilesData = await profilesResponse.json();
        dispatch(setProfiles(profilesData.profiles));
      } catch (error) {
        console.error('Failed to fetch profiles:', error);
      }
    }

    const fetchTeamsites = async () => {
      try {
        // Fetch Teamsites
        const teamsitesResponse = await fetch('/api/teamsites');
        if (!teamsitesResponse.ok) {
          console.error('Failed to fetch teamsites:', teamsitesResponse.statusText);
          return;
        }

        const teamsitesData = await teamsitesResponse.json();
        dispatch(setTeamsites(teamsitesData));
      } catch (error) {
        console.error('Failed to fetch teamsites:', error);
      }
    }

    const fetchPcnProperties = async () => {
      try {
        // Fetch PEN Properties
        const pcnResponse = await fetch('/api/pcn-properties');
        if (!pcnResponse.ok) {
          console.error('Failed to fetch PEN properties:', pcnResponse.statusText);
          return;
        }

        const pcnData = await pcnResponse.json();
        dispatch(setPcnProperties(pcnData.properties));
      } catch (error) {
        console.error('Failed to fetch PEN properties:', error);
      }
    }

    if (!isSessionExpired && !isPermissionDenied) {
      fetchPartners();
      fetchTeamsites();
      fetchProfiles();
      fetchPcnProperties();
      fetchContentSettings();
    }
  }
  , [dispatch, isSessionExpired, isPermissionDenied, currentTenant]);

  const routes: Record<string, string> = {
    'Explore Partners': '/available-partners',
    'My Connections': '/target',
    'Company Profile': '/profile',
    'Content Status': '/content',
    'Insights': '/insights',
    '401Error': '/401Error',
    'login': '/login',
    'home': '/'
  };

  const heroLogoSrc = companyProfile?.logo || fallbackLogo;

  return (
    <>
    <div style={{ backgroundColor: '#3C1A4B', color: '#FFFFFF', padding: '10px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height:'68px'}} >

        <div style={{display:'flex', gap:'10px', height:'24px'}}>
            <Image alt="Seismic" loading="lazy" width='20' height='0' style={{objectFit: 'cover', objectPosition: 'left'}} decoding="async" src="/assets/seismic-logo.svg" />
            <div>
              <p style={{cursor:'pointer', fontSize: '24px', marginTop: '2px', fontFamily:'Proxima Nova'}} onClick={async ()=> { 
                  setActiveLabel('home');
                  if(!isPermissionDenied){
                    if (pathname === "/") {
                      router.refresh();
                    } else {
                      router.push("/");
                    }
                  }
                }}>
                Partner Enablement Network
              </p>
            </div>
            {!isPermissionDenied && (
              <div  style={{marginLeft:'40px', display:'flex', flexDirection:'row', gap:'30px', alignItems:'center', justifyContent:'center'}}>
                {['Explore Partners', 'My Connections','Content Status', 'Insights'].map((label, index) => (
                  <div key={`Nav-${label}-${index}`}>
                      <LinkButton
                        style={{
                          color: 'white',
                          textDecoration: label === activeLabel ? 'underline' : 'none',
                          textUnderlineOffset: "6px",
                          fontWeight: 500,
                          fontSize: '24px',
                          fontFamily: 'Source Sans Pro',
                          lineHeight: '32px',
                        }}
                        size="large"
                        label={label}
                        onClick={async () => {
                          setActiveLabel(label);
                          router.push(routes[label]);
                        }}
                      />
                    </div>
                  ))}
              </div>)}
        </div>
        {
          !isPermissionDenied && (
            <div>
              <ButtonMenuContainer>
                  <ButtonMenuTrigger>
                      <span style={{borderColor:'unset', backgroundColor:'unset', color:'unset', padding:'5px'}} >
                          <IconCorporations  size={24} style={{ marginLeft:'4px'}} />
                          <IconArrowDown size={16} style={{marginLeft:'2px'}} />
                      </span>
                  </ButtonMenuTrigger>
                  <ButtonMenuMenu>
                      <ButtonMenuItem onActivate={async ()=> { 
                          router.push("/profile");
                          setActiveLabel('Company Profile');
                        }}>Company Profile</ButtonMenuItem>
                      <ButtonMenuItem onActivate={()=> signOut({callbackUrl: `/api/auth/logout?idToken=${idToken}&tenant=${currentTenant}`}) }>Logout</ButtonMenuItem>
                  </ButtonMenuMenu>
              </ButtonMenuContainer>
            </div>
          )
        }
    </div>
    { !isPermissionDenied && activeLabel==="home" && (
      <div style={{backgroundImage:`url("/assets/mockup-an-image-related-to-wealth-management-partners.png")`, height:'150px', width:'100%', justifyContent:'right', display:'flex', alignItems:'center'}}>
        <div style={{height:'70px', width:'310px', backgroundColor:'white', borderRadius:'5px', padding:'5px', marginRight:'50px', marginTop:'10px', display:'flex', justifyContent:'center'}}>
            <Image
              loading="lazy"
              alt='Logo'
              width={210}
              height={70}
              style={{objectFit: 'contain'}}
              src={heroLogoSrc}
            />
        </div>
      </div>
    )}
    </>
  );
};

export default Navbar;
